/**
 * Wallet modal: dropdown-style panel shown when the connected wallet button is clicked.
 * Displays: network chain, full address, Copy Address, Bucket Transfer, Disconnect.
 * Positioned below the wallet button.
 */
import React from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import "./WalletModal.css";

export interface WalletModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Display name for the connected RPC network chain */
  networkChainName: string;
  /** Full wallet address */
  walletAddress: string;
  /** Called when Copy Address is clicked */
  onCopyAddress?: () => void;
  /** Called when Bucket Transfer is clicked */
  onBucketTransfer?: () => void;
  /** Called when Coin Transfer is clicked (opens wallet-to-address transfer modal) */
  onCoinTransfer?: () => void;
  /** Called when Disconnect is clicked */
  onDisconnect?: () => void;
  /** Optional ref for the anchor button; used to position the modal below it */
  anchorRef?: React.RefObject<HTMLElement | null>;
}

function WalletModal({
  isOpen,
  onClose,
  networkChainName,
  walletAddress,
  onCopyAddress,
  onBucketTransfer,
  onCoinTransfer,
  onDisconnect,
  anchorRef,
}: WalletModalProps): JSX.Element | null {
  const handleCopyAddress = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress).then(
        () => toast.success("Address copied to clipboard"),
        () => toast.error("Failed to copy address"),
      );
    }
    onCopyAddress?.();
  };

  const handleBucketTransfer = () => {
    onClose();
    onBucketTransfer?.();
  };

  const handleCoinTransfer = () => {
    onClose();
    onCoinTransfer?.();
  };

  const handleDisconnect = () => {
    onClose();
    onDisconnect?.();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="wallet-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="wallet-modal-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Wallet"
      >
        <div className="wallet-modal-header">
          <h3>Wallet</h3>
        </div>

        <div className="wallet-modal-body">
          <div className="wallet-modal-row">
            <span className="wallet-modal-label">Network</span>
            <span className="wallet-modal-value">{networkChainName}</span>
          </div>

          <div className="wallet-modal-row">
            <span className="wallet-modal-label">Address</span>
            <span className="wallet-modal-address" title={walletAddress}>
              {walletAddress}
            </span>
          </div>

          <div className="wallet-modal-actions">
            <button
              type="button"
              className="wallet-modal-btn wallet-modal-btn-secondary"
              onClick={handleCopyAddress}
            >
              Copy Address
            </button>
            <button
              type="button"
              className="wallet-modal-btn wallet-modal-btn-secondary"
              onClick={handleBucketTransfer}
            >
              Bucket Transfer
            </button>
            <button
              type="button"
              className="wallet-modal-btn wallet-modal-btn-secondary"
              onClick={handleCoinTransfer}
            >
              Coin Transfer
            </button>
            <button
              type="button"
              className="wallet-modal-btn wallet-modal-btn-danger"
              onClick={handleDisconnect}
            >
              Disconnect
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
}

export default WalletModal;
